import { type DisplaySettings, type SavedGraphicMode } from '@verify/shared-utils';

export const MIN_TRANSPARENCY_OPACITY = 0.1;
export const MAX_TRANSPARENCY_OPACITY = 1;

/** Matches the session defaults in appState.ts and the inspection annotation default. */
export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  showTextHighlights: true,
  showDifferenceBoxes: true,
  hideOtherDifferences: true,
  showOverlay: true,
  grayscaleMode: false,
  singlePageView: false,
  showRepeats: true,
  showAnnotations: true,
  graphicEffectOn: true,
  graphicMode: 'flashing',
  transparencyOpacity: 0.5,
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readBoolean = (value: unknown, fallback: boolean): boolean => (typeof value === 'boolean' ? value : fallback);

const readGraphicMode = (value: unknown): SavedGraphicMode =>
  value === 'flashing' || value === 'transparentOverlay' ? value : DEFAULT_DISPLAY_SETTINGS.graphicMode;

const readOpacity = (value: unknown): number =>
  typeof value === 'number' &&
  Number.isFinite(value) &&
  value >= MIN_TRANSPARENCY_OPACITY &&
  value <= MAX_TRANSPARENCY_OPACITY
    ? value
    : DEFAULT_DISPLAY_SETTINGS.transparencyOpacity;

/**
 * Turn whatever Auth0 returned into a complete DisplaySettings.
 * A missing object, a missing field, a non-boolean, an unknown graphicMode, or an
 * opacity outside 0.1–1 falls back to that field's default. Unknown keys are ignored.
 */
export const sanitizeDisplaySettings = (raw: unknown): DisplaySettings => {
  if (!isRecord(raw)) return { ...DEFAULT_DISPLAY_SETTINGS };

  const defaults = DEFAULT_DISPLAY_SETTINGS;
  return {
    showTextHighlights: readBoolean(raw.showTextHighlights, defaults.showTextHighlights),
    showDifferenceBoxes: readBoolean(raw.showDifferenceBoxes, defaults.showDifferenceBoxes),
    hideOtherDifferences: readBoolean(raw.hideOtherDifferences, defaults.hideOtherDifferences),
    showOverlay: readBoolean(raw.showOverlay, defaults.showOverlay),
    grayscaleMode: readBoolean(raw.grayscaleMode, defaults.grayscaleMode),
    singlePageView: readBoolean(raw.singlePageView, defaults.singlePageView),
    showRepeats: readBoolean(raw.showRepeats, defaults.showRepeats),
    showAnnotations: readBoolean(raw.showAnnotations, defaults.showAnnotations),
    graphicEffectOn: readBoolean(raw.graphicEffectOn, defaults.graphicEffectOn),
    graphicMode: readGraphicMode(raw.graphicMode),
    transparencyOpacity: readOpacity(raw.transparencyOpacity),
  };
};
